/**
 * apps/server/client — Gestion des SAUVEGARDES
 * @author Dr Hamid MADANI <drmdh@msn.com>
 *
 * ─── CE QUE CETTE PAGE MET EN AVANT, ET POURQUOI ─────────────────────────────
 * Le bouton le plus important n'est pas « Sauvegarder » : c'est « Tester la restauration ».
 *
 * Une sauvegarde n'a de valeur que RESTAURÉE. On peut accumuler deux ans d'archives, les voir
 * s'afficher en vert, et découvrir le jour du sinistre qu'elles ne se rechargent pas. L'essai de
 * restauration rejoue l'archive dans une base JETABLE et RECOMPTE les enregistrements : c'est la
 * seule opération qui répond à « cette archive est-elle utilisable ? ». La vérification
 * d'intégrité, elle, dit seulement que les octets sont intacts — pas qu'ils se rechargent.
 *
 * La restauration RÉELLE, elle, est traitée comme ce qu'elle est : l'opération la plus destructrice
 * du produit. Elle exige de RECOPIER le nom de l'archive. Pas pour se protéger d'un attaquant (il a
 * déjà le jeton admin), mais de l'ACCIDENT — infiniment plus probable.
 */

import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const S = {
  page: { padding: 24, maxWidth: 980, margin: '0 auto' },
  carte: { background: '#fff', borderRadius: 12, padding: 20, marginBottom: 20, boxShadow: '0 2px 10px rgba(0,0,0,.07)' },
  ligne: { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' },
  champ: { padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1' },
  btn: { padding: '9px 16px', borderRadius: 8, border: 0, background: '#1d4ed8', color: '#fff', fontWeight: 600, cursor: 'pointer' },
  btnDoux: { padding: '7px 12px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' },
  btnDanger: { padding: '7px 12px', borderRadius: 8, border: 0, background: '#B91C1C', color: '#fff', cursor: 'pointer' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '.9rem' },
  td: { padding: '8px 6px', borderBottom: '1px solid #e2e8f0' },
  ok: { background: '#DCFCE7', border: '1px solid #16A34A', padding: 10, borderRadius: 8, marginBottom: 12 },
  ko: { background: '#FEE2E2', border: '1px solid #EF4444', padding: 10, borderRadius: 8, marginBottom: 12 },
  avert: { background: '#FEF3C7', border: '1px solid #F59E0B', padding: 12, borderRadius: 8, fontSize: '.9rem' },
};

const octets = (n) => (n > 1e6 ? `${(n / 1e6).toFixed(1)} Mo` : `${Math.round(n / 1024)} Ko`);
const quand = (s) => new Date(s).toLocaleString('fr-FR');

export default function Backup() {
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState('');

  const charger = useCallback(async () => {
    try {
      const r = await axios.get('/api/backup');
      setData(r.data.data);
      setCfg(r.data.data.config);
    } catch (e) {
      setMsg({ ko: e.response?.data?.error || e.message });
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const action = async (cle, fn) => {
    setBusy(cle); setMsg(null);
    try { await fn(); } catch (e) {
      setMsg({ ko: e.response?.data?.error || e.message });
    } finally { setBusy(''); }
  };

  const enregistrer = () => action('cfg', async () => {
    const r = await axios.put('/api/backup', cfg);
    setCfg(r.data.data);
    setMsg({ ok: 'Réglages enregistrés — la planification est relancée immédiatement.' });
    await charger();
  });

  const sauvegarder = () => action('run', async () => {
    const r = await axios.post('/api/backup/run');
    setMsg({ ok: `Sauvegarde créée : ${r.data.data.path} (${octets(r.data.data.size)})` });
    await charger();
  });

  const verifier = (p) => action(`v:${p}`, async () => {
    const r = await axios.post('/api/backup/verify', { path: p });
    const v = r.data.data;
    setMsg(v.integrity.ok
      ? { ok: `Intégrité OK — ${Object.entries(v.counts).map(([k, n]) => `${k}: ${n}`).join(', ')}` }
      : { ko: `Archive CORROMPUE (${v.integrity.reason})` });
  });

  const essayer = (p) => action(`t:${p}`, async () => {
    const r = await axios.post('/api/backup/test-restore', { path: p });
    const t = r.data.data;
    const total = Object.values(t.obtenus || {}).reduce((a, b) => a + b, 0);
    setMsg(t.verifie
      ? { ok: `Restauration d’essai RÉUSSIE — ${total} enregistrements relus dans une base jetable. Cette archive est utilisable.` }
      : { ko: 'Restauration d’essai ÉCHOUÉE : les compteurs ne coïncident pas. Cette archive n’est PAS fiable.' });
  });

  /**
   * Restauration réelle. Deux verrous côté serveur (`apply` + `confirm` = le chemin exact). Le
   * `prompt` ci-dessous n'est PAS la protection — il en est seulement la face visible : le serveur
   * refuse tout seul, même appelé en `curl`. Une IHM qui protège seule ne protège pas.
   */
  const restaurer = (p) => {
    const saisi = window.prompt(
      `RESTAURATION RÉELLE — cette opération ÉCRASE les données en cours.\n\n`
      + `Pour confirmer, recopiez exactement le nom de l’archive :\n${p}`,
    );
    if (saisi !== p) { setMsg({ ko: 'Restauration annulée (confirmation incorrecte).' }); return; }

    action(`r:${p}`, async () => {
      const r = await axios.post('/api/backup/restore', { path: p, apply: true, confirm: p });
      const t = r.data.data;
      const total = Object.values(t.obtenus || {}).reduce((a, b) => a + b, 0);
      setMsg({ ok: `Restauration effectuée — ${total} enregistrements réinjectés.` });
    });
  };

  if (!cfg) return <div style={S.page}><p>…</p></div>;

  const derniere = data?.lastRun;

  return (
    <div style={S.page}>
      <h1>Sauvegardes</h1>

      {msg?.ok && <div style={S.ok}>✅ {msg.ok}</div>}
      {msg?.ko && <div style={S.ko}>❌ {msg.ko}</div>}

      {/* ─── Planification ─── */}
      <div style={S.carte}>
        <h2 style={{ marginTop: 0 }}>Planification</h2>
        <div style={S.ligne}>
          <label>
            <input
              type="checkbox"
              checked={cfg.enabled}
              onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
            /> Sauvegardes automatiques
          </label>

          <select style={S.champ} value={cfg.mode} onChange={(e) => setCfg({ ...cfg, mode: e.target.value })}>
            <option value="daily">Chaque jour</option>
            <option value="weekly">Chaque semaine</option>
            <option value="monthly">Chaque mois</option>
          </select>

          <input
            style={S.champ} type="time" value={cfg.time}
            onChange={(e) => setCfg({ ...cfg, time: e.target.value })}
          />

          <label>
            Conserver
            <input
              style={{ ...S.champ, width: 70, marginLeft: 6 }} type="number" min={1}
              value={cfg.keepLast}
              onChange={(e) => setCfg({ ...cfg, keepLast: Number(e.target.value) })}
            /> archives
          </label>
        </div>

        <div style={{ ...S.ligne, marginTop: 12 }}>
          <label style={{ flex: 1 }}>
            Dossier
            <input
              style={{ ...S.champ, width: '100%' }} value={cfg.dossier}
              onChange={(e) => setCfg({ ...cfg, dossier: e.target.value })}
            />
          </label>
        </div>

        <p style={{ color: '#64748b', fontSize: '.85rem' }}>
          Chaque sauvegarde automatique est suivie d’une <b>restauration d’essai</b> dans une base
          jetable. Les anciennes archives ne sont supprimées <b>qu’après</b> qu’une nouvelle ait été
          créée <i>et</i> vérifiée — on ne détruit jamais la dernière copie valide.
        </p>

        <div style={S.ligne}>
          <button style={S.btn} onClick={enregistrer} disabled={busy === 'cfg'}>
            {busy === 'cfg' ? 'Enregistrement…' : 'Enregistrer'}
          </button>
          <button style={S.btnDoux} onClick={sauvegarder} disabled={busy === 'run'}>
            {busy === 'run' ? 'Sauvegarde en cours…' : '💾 Sauvegarder maintenant'}
          </button>
        </div>

        {derniere && (
          <p style={{ marginTop: 12, fontSize: '.88rem' }}>
            Dernier cycle automatique : <b>{derniere.ok ? '✅ réussi' : '❌ échoué'}</b> le {quand(derniere.at)}
            {derniere.restored != null && ` — ${derniere.restored} enregistrements relus à l’essai`}
            {derniere.error && <span style={{ color: '#B91C1C' }}> — {derniere.error}</span>}
          </p>
        )}
      </div>

      {/* ─── Archives ─── */}
      <div style={S.carte}>
        <h2 style={{ marginTop: 0 }}>Archives ({data?.backups?.length ?? 0})</h2>

        {!data?.backups?.length ? (
          <p style={{ color: '#64748b' }}>Aucune sauvegarde pour l’instant.</p>
        ) : (
          <table style={S.table}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Date</th>
                <th style={{ textAlign: 'left' }}>Taille</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.backups.map((b) => (
                <tr key={b.path}>
                  <td style={S.td}>{quand(b.createdAt)}</td>
                  <td style={S.td}>{octets(b.size)}</td>
                  <td style={{ ...S.td, textAlign: 'right' }}>
                    <button style={S.btnDoux} onClick={() => verifier(b.path)} disabled={busy === `v:${b.path}`}>
                      Vérifier l’intégrité
                    </button>{' '}
                    {/* Le bouton qui compte : il répond à « cette archive est-elle UTILISABLE ? ». */}
                    <button style={{ ...S.btnDoux, fontWeight: 700 }} onClick={() => essayer(b.path)} disabled={busy === `t:${b.path}`}>
                      {busy === `t:${b.path}` ? 'Essai…' : '🧪 Tester la restauration'}
                    </button>{' '}
                    <button style={S.btnDanger} onClick={() => restaurer(b.path)} disabled={busy === `r:${b.path}`}>
                      Restaurer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div style={{ ...S.avert, marginTop: 16 }}>
          ⚠️ <b>Restaurer</b> écrase les données en cours. Il faudra recopier le nom exact de
          l’archive pour confirmer. Avant toute restauration réelle, utilisez
          <b> Tester la restauration</b> : c’est sans risque, et c’est la seule opération qui
          prouve que l’archive est réellement utilisable.
        </div>
      </div>
    </div>
  );
}
